<template>
	<span>
		<b-badge
			variant="warning"
			class="mr-1"
			:id="popoverId"
			tabindex="0"
			@mouseenter="fetchFlightData"
			@focus="fetchFlightData"
		>
			<md-icon icon="flight" style="font-size: 14px; vertical-align: middle;" />
			{{ tag.text }}
		</b-badge>
		<b-badge
			v-if="showDot && item.monitoredFlight === 'not_monitored'"
			variant="secondary"
			class="mr-1"
			style="font-size: 12px;"
		>
			{{ $t('flightInfo.noMonitoring') }}
		</b-badge>
		<!-- Cancelled Badge -->
		<b-badge
			v-if="showCancelledBadge"
			variant="danger"
			class="mr-1"
			style="font-size: 10px;"
		>
			<md-icon icon="cancel" style="font-size: 12px; vertical-align: middle;" />
			{{ $t('flightInfo.cancelled') }}
		</b-badge>
		<!-- Delay/Early Status Badge -->
		<b-badge
			v-if="showDelayBadge"
			variant="warning"
			class="mr-1"
			style="font-size: 10px;"
		>
			<md-icon :icon="delayBadgeIcon" style="font-size: 12px; vertical-align: middle;" />
			{{ delayStatusText }}
		</b-badge>
		<!-- Delay Time Badge -->
		<b-badge
			v-if="showDelayBadge"
			:variant="delayTimeVariant"
			class="mr-1"
			style="font-size: 10px;"
		>
			{{ delayTimeText }}
		</b-badge>
		<!-- On Time Badge -->
		<b-badge
			v-if="showOnTimeBadge"
			variant="success"
			class="mr-1"
			style="font-size: 10px;"
		>
			<md-icon icon="check_circle" style="font-size: 12px; vertical-align: middle;" />
			{{ $t('flightInfo.onTime') }}
		</b-badge>
		<b-popover
			:target="popoverId"
			triggers="hover focus"
			placement="auto"
			variant="info"
		>
			<template #title>
				<md-icon icon="flight" /> {{ $t('flightInfo.title') }}
			</template>
			<div v-if="loading" class="text-center p-2">
				<b-spinner small variant="primary"></b-spinner>
				<span class="ml-2">{{ $t('flightInfo.loading') }}</span>
			</div>
			<div v-else-if="flightData && flightData.noData || flightData && flightData.error" class="text-muted">
				{{ $t('flightInfo.noFlightInfo') }}
			</div>
			<div v-else-if="flightData && flightData.data" style="min-width: 250px;">
				<div class="mb-2">
					<strong>{{ flightData.flightNumber }}</strong>
					<span v-if="flightData.data.ident_icao" class="text-muted ml-1">({{ flightData.data.ident_icao }})</span>
					<b-badge :variant="statusVariant" class="ml-2">
						{{ flightData.data.status }}
					</b-badge>
					<b-badge v-if="flightData.landingStatus === 'should_have_landed'" variant="warning" class="ml-1">
						<md-icon icon="flight_land" style="font-size: 12px; vertical-align: middle;" />
						{{ $t('flightInfo.checkLanding') }}
					</b-badge>
					<b-badge v-if="flightData.data.cancelled" variant="danger" class="ml-1">
						<md-icon icon="cancel" style="font-size: 12px; vertical-align: middle;" />
						{{ $t('flightInfo.cancelled') }}
					</b-badge>
				</div>
				<!-- Delay/Early Status -->
				<div class="mb-2" v-if="hasArrivalDelay">
					<span v-if="flightData.data.arrival_delay < 0" class="text-success">
						<md-icon icon="schedule" style="font-size: 14px;" />
						<strong>{{ $t('flightInfo.early') }}:</strong> {{ formatDelayTime(flightData.data.arrival_delay) }}
					</span>
					<span v-else-if="flightData.data.arrival_delay > 0" class="text-danger">
						<md-icon icon="schedule" style="font-size: 14px;" />
						<strong>{{ $t('flightInfo.delayed') }}:</strong> {{ formatDelayTime(flightData.data.arrival_delay) }}
					</span>
					<span v-else class="text-success">
						<md-icon icon="check_circle" style="font-size: 14px;" />
						<strong>{{ $t('flightInfo.onTime') }}</strong>
					</span>
				</div>
				<!-- Progress Bar -->
				<div class="mb-2" v-if="hasProgressPercent" style="max-width: 280px;">
					<small><strong>{{ $t('flightInfo.progress') }}:</strong> {{ flightData.data.progress_percent }}%</small>
					<div style="background-color: #e9ecef; border-radius: 4px; height: 6px; max-width: 100%;">
						<div :style="progressBarStyle"></div>
					</div>
				</div>
				<!-- Origin/Destination -->
				<div class="d-flex justify-content-between align-items-center mb-2">
					<div class="text-center">
						<div class="font-weight-bold">{{ originIata }}</div>
						<small class="text-muted">{{ originCity }}</small>
					</div>
					<div class="text-center">
						<md-icon icon="arrow_forward" />
						<div v-if="flightData.data.filed_ete" class="small text-muted">
							{{ formatFlightDuration(flightData.data.filed_ete) }}
						</div>
					</div>
					<div class="text-center">
						<div class="font-weight-bold">{{ destinationIata }}</div>
						<small class="text-muted">{{ destinationCity }}</small>
					</div>
				</div>
				<hr class="my-2" />
				<!-- Horarios -->
				<div class="small mb-2">
					<div v-if="flightData.flightDatetime" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="event" style="font-size: 14px; color: #6c757d;" /> {{ $t('flightInfo.originalFlight') }}</span>
						<strong>{{ formatFlightDateTime(flightData.flightDatetime) }}</strong>
					</div>
					<!-- Hidden temporarily: departure times
					<div class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="flight_takeoff" style="font-size: 14px; color: #6c757d;" /> {{ $t('flightInfo.scheduledDeparture') }}</span>
						<strong>{{ formatFlightTime(flightData.data.scheduled_off) }}</strong>
					</div>
					<div v-if="flightData.data.actual_off" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="flight_takeoff" style="font-size: 14px; color: #28a745;" /> {{ $t('flightInfo.actualDeparture') }}</span>
						<strong class="text-success">{{ formatFlightTime(flightData.data.actual_off) }}</strong>
					</div>
					<div v-if="flightData.data.departure_delay" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="schedule" style="font-size: 14px;" :style="{ color: flightData.data.departure_delay > 0 ? '#dc3545' : '#28a745' }" /> {{ $t('flightInfo.departureDelay') }}</span>
						<strong :class="flightData.data.departure_delay > 0 ? 'text-danger' : 'text-success'">{{ formatDelayTime(flightData.data.departure_delay) }}</strong>
					</div>
					-->
					<div v-if="flightData.data.estimated_on" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="flight_land" style="font-size: 14px; color: #6c757d;" /> {{ $t('flightInfo.estimatedLanding') }}</span>
						<strong>{{ formatFlightTime(flightData.data.estimated_on) }}</strong>
					</div>
					<div v-if="flightData.data.actual_on" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="flight_land" style="font-size: 14px; color: #28a745;" /> {{ $t('flightInfo.actualLanding') }}</span>
						<strong class="text-success">{{ formatFlightTime(flightData.data.actual_on) }}</strong>
					</div>
					<div v-if="flightData.data.estimated_in" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="door_front" style="font-size: 14px; color: #6c757d;" /> {{ $t('flightInfo.estimatedGateArrival') }}</span>
						<strong>{{ formatFlightTime(flightData.data.estimated_in) }}</strong>
					</div>
					<div v-if="flightData.data.actual_in" class="d-flex justify-content-between py-1" style="border-bottom: 1px solid #eee;">
						<span><md-icon icon="door_front" style="font-size: 14px; color: #28a745;" /> {{ $t('flightInfo.actualGateArrival') }}</span>
						<strong class="text-success">{{ formatFlightTime(flightData.data.actual_in) }}</strong>
					</div>
				</div>
				<!-- Detalles -->
				<div class="small d-flex flex-wrap">
					<div v-if="flightData.data.gate_destination" class="mr-3 mb-1">
						<span class="text-muted">{{ $t('flightInfo.arrivalGate') }}:</span> <strong>{{ flightData.data.gate_destination }}</strong>
					</div>
					<div v-if="flightData.data.terminal_destination" class="mr-3 mb-1">
						<span class="text-muted">{{ $t('flightInfo.terminal') }}:</span> <strong>{{ flightData.data.terminal_destination }}</strong>
					</div>
					<div v-if="flightData.data.baggage_claim" class="mb-1">
						<span class="text-muted">{{ $t('flightInfo.baggage') }}:</span> <strong>{{ flightData.data.baggage_claim }}</strong>
					</div>
				</div>
				<!-- Data Source & Last Update -->
				<div v-if="flightData.dataSource || flightData.lastUpdateAt" class="text-right mt-2 small text-muted">
					<div v-if="flightData.lastUpdateAt">
						<md-icon icon="update" style="font-size: 12px;" /> {{ formatFlightDateTime(flightData.lastUpdateAt) }}
					</div>
					<div v-if="flightData.dataSource">
						<md-icon icon="source" style="font-size: 12px;" /> {{ flightData.dataSource }}
					</div>
				</div>
			</div>
			<div v-else class="text-muted">
				{{ $t('flightInfo.hoverToLoad') }}
			</div>
		</b-popover>
	</span>
</template>

<script>
import api from "@/api.js";
import moment from "moment";

export default {
	name: "FlightInfoPopover",
	props: {
		tag: {
			type: Object,
			required: true
		},
		item: {
			type: Object,
			required: true
		},
		index: {
			type: Number,
			required: true
		},
		autoLoad: {
			type: Boolean,
			default: false
		},
		showDot: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			flightData: null,
			loading: false
		};
	},
	mounted() {
		if (this.autoLoad) {
			this.fetchFlightData();
		}
	},
	computed: {
		popoverId() {
			return `flight-popover-${this.item.id}-${this.index}`;
		},
		airportIata() {
			if (this.item.pickup && this.item.pickup.airportIata) {
				return this.item.pickup.airportIata;
			}
			if (this.item.dropoff && this.item.dropoff.airportIata) {
				return this.item.dropoff.airportIata;
			}
			return null;
		},
		hasArrivalDelay() {
			return this.flightData?.data?.arrival_delay !== null &&
				this.flightData?.data?.arrival_delay !== undefined;
		},
		hasProgressPercent() {
			return this.flightData?.data?.progress_percent !== null &&
				this.flightData?.data?.progress_percent !== undefined;
		},
		statusVariant() {
			if (this.flightData?.flightStatus === 'early') return 'success';
			if (this.flightData?.flightStatus === 'late') return 'danger';
			return 'info';
		},
		progressBarStyle() {
			const percent = this.flightData?.data?.progress_percent || 0;
			return {
				width: percent + '%',
				height: '100%',
				backgroundColor: percent >= 100 ? '#28a745' : '#007bff',
				borderRadius: '4px'
			};
		},
		originIata() {
			return this.flightData?.data?.origin?.code_iata || '-';
		},
		originCity() {
			return this.flightData?.data?.origin?.city || '';
		},
		destinationIata() {
			return this.flightData?.data?.destination?.code_iata || '-';
		},
		destinationCity() {
			return this.flightData?.data?.destination?.city || '';
		},
		showCancelledBadge() {
			return this.flightData?.data?.cancelled === true;
		},
		minDelayThresholdSeconds() {
			// Convert minDelayThreshold (minutes) to seconds, default to 0
			const threshold = this.flightData?.minDelayThreshold || 0;
			return threshold * 60;
		},
		isDelaySignificant() {
			const delay = this.flightData?.data?.arrival_delay;
			if (delay === null || delay === undefined) return false;
			return Math.abs(delay) > this.minDelayThresholdSeconds;
		},
		showDelayBadge() {
			if (this.showCancelledBadge) return false;
			return this.hasArrivalDelay && this.isDelaySignificant;
		},
		showOnTimeBadge() {
			if (this.showCancelledBadge) return false;
			if (!this.flightData?.data) return false;
			// Show "on time" when delay is within threshold
			return this.hasArrivalDelay &&
				!this.isDelaySignificant &&
				this.flightData?.flightStatus !== 'not_found';
		},
		delayTimeVariant() {
			const delay = this.flightData?.data?.arrival_delay;
			if (!delay) return 'info';
			// Green for early (negative delay), Red for late (positive delay)
			return delay < 0 ? 'success' : 'danger';
		},
		delayBadgeIcon() {
			const delay = this.flightData?.data?.arrival_delay;
			if (delay < 0) return 'fast_forward';
			return 'schedule';
		},
		delayStatusText() {
			const delay = this.flightData?.data?.arrival_delay;
			if (!delay) return '';
			return delay < 0 ? this.$t('flightInfo.early') : this.$t('flightInfo.late');
		},
		delayTimeText() {
			const delay = this.flightData?.data?.arrival_delay;
			if (!delay) return '';
			const prefix = delay < 0 ? '-' : '+';
			return prefix + this.formatDelayTime(delay);
		}
	},
	methods: {
		fetchFlightData() {
			if (this.flightData || this.loading) {
				return;
			}

			const transportServiceId = this.item.id
			const flightNumber = this.item.flightNumber;
			const flightDate = this.item.flightDatetime;

			if (!flightNumber || !flightDate) {
				return;
			}

			this.loading = true;

			api(this)
				.get("/flights/data", {
					params: {
						airportIata: this.airportIata,
						flightNumber,
						flightDate,
						transportServiceId
					}
				})
				.then(res => {
					if (res.data && res.data.success && res.data.flight 
						&& res.data.flight.flightStatus != 'not_found') {
						this.flightData = res.data.flight;
					} else {
						this.flightData = { noData: true };
					}
				})
				.catch(() => {
					this.flightData = { error: true };
				})
				.finally(() => {
					this.loading = false;
				});
		},
		formatFlightTime(datetime) {
			if (!datetime) return '-';
			return moment(datetime).format("HH:mm");
		},
		formatFlightDateTime(datetime) {
			if (!datetime) return '-';
			return moment(datetime).format("DD/MM/YY HH:mm");
		},
		formatDelayTime(delaySeconds) {
			if (delaySeconds === null || delaySeconds === undefined) return '-';
			const absSeconds = Math.abs(delaySeconds);
			const hours = Math.floor(absSeconds / 3600);
			const minutes = Math.floor((absSeconds % 3600) / 60);

			if (hours > 0 && minutes > 0) {
				return `${hours}h ${minutes}min`;
			} else if (hours > 0) {
				return `${hours}h`;
			} else {
				return `${minutes}min`;
			}
		},
		formatFlightDuration(seconds) {
			if (!seconds) return '-';
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			if (hours > 0 && minutes > 0) {
				return `${hours}h ${minutes}m`;
			} else if (hours > 0) {
				return `${hours}h`;
			} else {
				return `${minutes}m`;
			}
		}
	}
};
</script>
